import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '@/auth/msalInstance';
import { tokenRequest } from '@/auth/msalConfig';

const API_SCOPES: string[] = [
  String(import.meta.env.VITE_MSAL_API_SCOPE ?? 'api://default/.default'),
];

// Guards against firing several redirects when concurrent requests all hit the same failure.
let reauthenticationStarted = false;

function startReauthentication(): void {
  if (reauthenticationStarted) {
    return;
  }
  reauthenticationStarted = true;

  msalInstance
    .acquireTokenRedirect({ scopes: API_SCOPES, state: window.location.href })
    .catch((redirectError: unknown) => {
      // Another interaction is already in progress, or the redirect could not start.
      reauthenticationStarted = false;
      if (import.meta.env.DEV) {
        console.warn('Re-authentication redirect failed:', redirectError);
      }
    });
}

/**
 * Returns an API access token, or `null` when none can be obtained (no signed-in account or
 * acquisition failed). Callers must treat `null` as "do not send the request"; when silent
 * renewal needs user interaction, a re-authentication redirect is started.
 */
export async function getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  try {
    const account = msalInstance.getActiveAccount();

    if (!account) {
      return null;
    }

    const response = await msalInstance.acquireTokenSilent({
      scopes: API_SCOPES,
      account,
      ...(options?.forceRefresh ? { forceRefresh: true } : {}),
    });

    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      startReauthentication();
    }
    if (import.meta.env.DEV) {
      console.warn('Token acquisition failed:', error);
    }
    return null;
  }
}

export function getActiveAccount() {
  return msalInstance.getActiveAccount();
}

/**
 * Acquires a Microsoft Graph access token (the `tokenRequest` scope) for the active
 * account, or `null` when no account is signed in / acquisition fails. Token acquisition
 * is kept in the auth/token layer so store API modules need not import the MSAL singleton.
 */
export async function acquireGraphToken(): Promise<string | null> {
  try {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account,
    });

    return response.accessToken;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Graph token acquisition failed:', error);
    }
    return null;
  }
}
